import { ChangeEvent, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import clsx from 'clsx';
import * as R from 'ramda';

import textConstants from '@atom/constants/textConstants';
import { WORK_ORDER_BULK_ASSET_FILTER_CREATE } from '@atom/graph/work';
import { useNavigation } from '@atom/hooks/useNavigation';
import { useSchemas } from '@atom/hooks/useSchemas';
import {
  Button,
  Checkbox,
  DatePicker,
  Icon,
  List,
  Modal,
  Select,
  Snackbar,
  TextField,
} from '@atom/mui';
import { EventIcon } from '@atom/mui/icons';
import { PolicyAction } from '@atom/types/policy';
import {
  WorkOrderCustomDueDate,
  WorkOrderCustomDueDateStatus,
  WorkOrderCustomDueDateUnit,
} from '@atom/types/work';
import {
  SearchWorkTemplate,
  WorkTemplateListItem,
} from '@atom/types/workTemplate';
import { formatAssetAttributeFilters } from '@atom/utilities/assetUtilities';
import { convertDateToMillisGMTMidday } from '@atom/utilities/timeUtilities';
import {
  getErrorCode,
  isNilOrEmpty,
} from '@atom/utilities/validationUtilities';
import { getWorkTypeIconSvg } from '@atom/utilities/workTypeIconUtilities';

import CreateWorkTemplateSelection from './CreateWorkTemplateSelection';

const { MenuItem } = Select;

import classes from './createWorkModals.module.css';

const { ListItem, ListItemText } = List;

enum View {
  WORK_TEMPLATE,
  DUE_DATE,
}

type WorkTemplate = WorkTemplateListItem | SearchWorkTemplate;

interface Props {
  open: boolean;
  onClose: () => void;
  schemaId: string;
  count: number;
  filterState: any;
}

const styles = {
  modal: {
    display: 'flex',
    flexDirection: 'column',
    padding: '2rem',
  },
  icon: {
    marginRight: '2rem',
  },
  duration: {
    width: '2rem',
  },
  unit: {
    width: '5rem',
  },
  status: {
    width: '6rem',
  },
};

const DEFAULT_CUSTOM_DUE_DATE: WorkOrderCustomDueDate = {
  duration: 1,
  unit: WorkOrderCustomDueDateUnit.Year,
  status: WorkOrderCustomDueDateStatus.Completed,
};

const CreateWorkForAssetsModal = ({
  open,
  onClose,
  schemaId,
  count,
  filterState,
}: Props) => {
  const [view, setView] = useState<View>(View.WORK_TEMPLATE);
  const [dueDate, setDueDate] = useState<Date>(new Date());
  const [customDueDate, setCustomDueDate] = useState<WorkOrderCustomDueDate>();
  const [workTemplate, setWorkTemplate] = useState<WorkTemplate>(null);
  const [, { navigate }] = useNavigation();

  const { schemas = [] } = useSchemas();

  const workTemplateSchema = useMemo(() => {
    return schemas.find(({ id }) => id === workTemplate?.schemaId);
  }, [workTemplate]);

  const [bulkCreateWork, { loading }] = useMutation(
    WORK_ORDER_BULK_ASSET_FILTER_CREATE,
  );

  const resetState = () => {
    setView(View.WORK_TEMPLATE);
    setDueDate(new Date());
    setCustomDueDate(null);
    setWorkTemplate(null);
  };

  const handleSubmit = async () => {
    try {
      Snackbar.info({
        message:
          'Work creation is in progress and may take a while to complete.',
      });

      const res = await bulkCreateWork({
        variables: {
          // @ts-ignore
          input: R.reject(isNilOrEmpty, {
            customDueDate,
            dueDate: dueDate ? convertDateToMillisGMTMidday(dueDate) : null,
            workTemplateId: workTemplate.id,
            schemaId,
            categoryIds: filterState?.categoryIdsFilters,
            hasPendingChangesOnly: filterState?.hasPendingChangesOnly,
            attributes: formatAssetAttributeFilters(filterState?.assetFilters),
          }),
        },
      });

      Snackbar.info({
        message: `Created ${
          res?.data?.workOrderBulkAssetFilterCreate?.totalWorkOrders || 0
        } work.`,
        action: 'View',
        onActionClick: () => navigate('/workOrders'),
      });

      onClose();
    } catch (error) {
      if (getErrorCode(error) === 422) {
        Snackbar.error({
          message:
            'Cannot create work because work required fields are missing.',
        });
      } else {
        Snackbar.error({
          message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
        });
      }
    }
  };

  const handleCustomDueDateChange = (
    update: Partial<WorkOrderCustomDueDate>,
  ) => {
    setCustomDueDate(current => ({ ...current, ...update }));
  };

  const handleCustomDateToggle = () => {
    if (R.isNil(customDueDate)) {
      setCustomDueDate(DEFAULT_CUSTOM_DUE_DATE);
      setDueDate(null);
    } else {
      setCustomDueDate(null);
      setDueDate(new Date());
    }
  };

  const createDisabled =
    isNilOrEmpty(workTemplate) ||
    loading ||
    (isNilOrEmpty(dueDate) && isNilOrEmpty(customDueDate));

  const getFooter = () => {
    return view === View.WORK_TEMPLATE ? (
      <Button onClick={onClose} data-cy="createWorkForAssetsCancelButton">
        Cancel
      </Button>
    ) : (
      <div className={classes.footer}>
        <div>
          <Button onClick={resetState} data-cy="createWorkForAssetsBackButton">
            Back
          </Button>
        </div>
        <div>
          <Button
            onClick={onClose}
            sx={{ marginRight: '0.5rem' }}
            data-cy="createWorkForAssetsCancelButton"
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={createDisabled}
            onClick={handleSubmit}
            data-cy="createWorkForAssetsCreateButton"
          >
            Create
          </Button>
        </div>
      </div>
    );
  };

  const headerText = R.isNil(customDueDate)
    ? `You are creating ${count} work for inventory in the filter results. You can only create work for top-level inventory.`
    : 'You can only create work for top-level inventory.';

  return (
    <Modal
      title="Create Work for Filter Results"
      open={open}
      footer={getFooter()}
      onExited={resetState}
      data-cy="createWorkForAssetsModal"
      contentStyle={
        view === View.WORK_TEMPLATE
          ? { ...styles.modal, height: '50vh', padding: '2rem 3.875rem' }
          : styles.modal
      }
    >
      <div style={{ marginBottom: '1.25rem' }}>{headerText} </div>
      {view === View.WORK_TEMPLATE ? (
        <CreateWorkTemplateSelection
          onClick={template => {
            setWorkTemplate(template);
            setView(View.DUE_DATE);
          }}
          label="Start with a work template:"
          emptyContentLabel="There are 0 matching work templates."
          schemaId={schemaId}
          hasSchema={false}
          action={PolicyAction.BULK_CREATE}
          multiple
        />
      ) : (
        <>
          {workTemplate && (
            <div className={clsx(classes.modalRow, classes.workTemplate)}>
              <Icon style={styles.icon}>
                <img
                  src={getWorkTypeIconSvg(
                    workTemplate?.isParent,
                    workTemplate?.parentId,
                  )}
                />
              </Icon>
              <ListItem disableGutters style={{ margin: 0, padding: 0 }}>
                <ListItemText
                  style={{ margin: 0 }}
                  primary={workTemplate.name}
                  secondary={workTemplateSchema?.name}
                />
              </ListItem>
            </div>
          )}
          <div className={classes.modalRow}>
            <Icon icon={EventIcon} style={styles.icon} />
            <DatePicker
              name="dueDate"
              label="Due Date"
              value={dueDate}
              onChange={(date: Date) => setDueDate(date || null)}
              disabled={!R.isNil(customDueDate)}
              data-cy="createWorkForAssetsDueDateInput"
            />
          </div>
          <div className={clsx(classes.modalRow, classes.checkbox)}>
            <Checkbox
              checked={!R.isNil(customDueDate)}
              onChange={handleCustomDateToggle}
              data-cy="createWorkForAssetsCustomDueDateCheckbox"
            />
            <div className={classes.modalCheckboxLabel}>Custom Due Date</div>
          </div>
          {!R.isNil(customDueDate) && (
            <div className={classes.modalCustomDueDate}>
              <TextField
                name="duration"
                type="number"
                slotProps={{
                  htmlInput: {
                    min: 1,
                  },
                }}
                style={styles.duration}
                value={customDueDate?.duration || ''}
                onChange={({ target: { value } }) =>
                  handleCustomDueDateChange({
                    duration: value ? +value : null,
                  })
                }
                data-cy="createWorkForAssetsDurationInput"
              />
              <Select
                style={styles.unit}
                value={customDueDate?.unit}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleCustomDueDateChange({
                    unit: event.target.value as WorkOrderCustomDueDateUnit,
                  })
                }
                data-cy="createWorkForAssetsUnitSelect"
              >
                {Object.entries(WorkOrderCustomDueDateUnit).map(
                  ([key, value]) => (
                    <MenuItem key={key} value={value}>
                      {customDueDate?.duration === 1 ? key : `${key}s`}
                    </MenuItem>
                  ),
                )}
              </Select>
              <div className={classes.modalCustomDateLabel}>After work is</div>
              <Select
                style={styles.status}
                value={customDueDate?.status}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleCustomDueDateChange({
                    status: event.target.value as WorkOrderCustomDueDateStatus,
                  })
                }
                data-cy="createWorkForAssetsStatusSelect"
              >
                {Object.entries(WorkOrderCustomDueDateStatus).map(
                  ([key, value]) => (
                    <MenuItem key={key} value={value}>
                      {key}
                    </MenuItem>
                  ),
                )}
              </Select>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default CreateWorkForAssetsModal;
